import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Alert, Form, Modal } from 'react-bootstrap';
import { buildMediaUrl, helpdeskAPI } from '../services/api';
import useTicketWebSocket from '../hooks/useTicketWebSocket';
import AICopilotCard from './AICopilotCard';

const CHANNEL_LABELS = {
  web: 'Web',
  telegram: 'Telegram',
  messenger: 'Messenger',
  whatsapp: 'WhatsApp',
  email: 'Email',
  api: 'API',
};

const CHANNEL_ICON_CLASSES = {
  web: 'fas fa-globe',
  telegram: 'fab fa-telegram',
  messenger: 'fab fa-facebook-messenger',
  whatsapp: 'fab fa-whatsapp',
  email: 'fas fa-envelope',
  api: 'fas fa-code',
};

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [saveAsKnowledge, setSaveAsKnowledge] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(() => window.innerWidth < 992);
  const [showTicketInfo, setShowTicketInfo] = useState(() => window.innerWidth >= 992);

  const latestAISupport = ticket?.ai_support?.latest || null;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobileLayout(mobile);

      if (!mobile) {
        setShowTicketInfo(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // WebSocket handler for new comments
  const handleNewComment = useCallback((newComment) => {
    setTicket(prevTicket => {
      if (!prevTicket) return prevTicket;
      
      // Check if comment already exists
      const commentExists = prevTicket.comments.some(c => c.id === newComment.id);
      if (commentExists) return prevTicket;
      
      // Add new comment and sort chronologically
      const updatedComments = [...prevTicket.comments, newComment].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      
      // Show notification
      setHasNewMessage(true);
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE');
      audio.volume = 0.3;
      audio.play().catch(() => {});
      setTimeout(() => setHasNewMessage(false), 3000);
      
      return { ...prevTicket, comments: updatedComments };
    });
  }, []);

  // WebSocket handler for ticket updates (assignment, status changes)
  const handleTicketUpdate = useCallback((updatedTicket) => {
    setTicket(prevTicket => {
      if (!prevTicket) return prevTicket;
      
      // Merge updated ticket data while preserving existing comments order
      if (updatedTicket.comments) {
        updatedTicket.comments.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      }
      
      return updatedTicket;
    });
  }, []);

  // Connect to WebSocket
  useTicketWebSocket(id, handleNewComment, handleTicketUpdate);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.comments]);

  const fetchTicket = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await helpdeskAPI.getTicket(id);
      // Sort comments by created_at to ensure chronological order
      if (response.data.comments) {
        response.data.comments.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      }
      setTicket(response.data);
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError('Failed to load ticket details');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!commentText.trim() && selectedFiles.length === 0) {
      return;
    }

    const shouldSaveAsKnowledge = saveAsKnowledge && !isInternalNote && commentText.trim();

    try {
      setSubmittingComment(true);

      // Create FormData if files are selected
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append('content', commentText || 'Attachment');
        formData.append('is_internal', isInternalNote);

        // Append each file
        selectedFiles.forEach((file) => {
          formData.append('files', file);
        });

        await helpdeskAPI.addComment(id, formData);
      } else {
        // Send as JSON if no files
        await helpdeskAPI.addComment(id, {
          content: commentText,
          is_internal: isInternalNote
        });
      }

      if (shouldSaveAsKnowledge) {
        try {
          await helpdeskAPI.createKnowledgeEntry({
            question_summary: (ticket?.title || ticket?.description || '').slice(0, 300),
            answer: commentText,
            category_tag: ticket?.category?.name || latestAISupport?.module || '',
            source_ticket: id,
          });
        } catch (kbErr) {
          console.error('Error saving knowledge entry:', kbErr);
        }
      }

      setCommentText('');
      setIsInternalNote(false);
      setSaveAsKnowledge(false);
      setSelectedFiles([]);
      fetchTicket(); // Refresh to show new comment
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleUseAIDraftReply = (draftReply) => {
    const draft = draftReply || latestAISupport?.draft_reply;
    if (!draft) {
      return;
    }
    setCommentText(draft);
    setIsInternalNote(false);
  };

  const handleCloseTicket = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) {
      return;
    }

    try {
      setActionLoading(true);
      await helpdeskAPI.closeTicket(id);
      fetchTicket(); // Refresh ticket data
    } catch (err) {
      console.error('Error closing ticket:', err);
      setError('Failed to close ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopenTicket = async () => {
    try {
      setActionLoading(true);
      await helpdeskAPI.reopenTicket(id);
      fetchTicket(); // Refresh ticket data
    } catch (err) {
      console.error('Error reopening ticket:', err);
      setError('Failed to reopen ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignTicket = async () => {
    if (!selectedUser) {
      setError('Please select a user to assign');
      return;
    }

    try {
      setActionLoading(true);
      const response = await helpdeskAPI.assignTicket(id, selectedUser);
      setTicket(response.data);  // Update with new data
      setShowAssignModal(false);
      setSelectedUser('');
      setError(null);
    } catch (err) {
      console.error('Error assigning ticket:', err);
      setError(err.response?.data?.error || 'Failed to assign ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeStatus = async () => {
    if (!selectedStatus) {
      setError('Please select a status');
      return;
    }

    try {
      setActionLoading(true);
      const response = await helpdeskAPI.changeStatus(id, selectedStatus);
      setTicket(response.data);  // Update with new data
      setShowStatusModal(false);
      setSelectedStatus('');
      setError(null);
    } catch (err) {
      console.error('Error changing status:', err);
      setError(err.response?.data?.error || 'Failed to change status');
    } finally {
      setActionLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await helpdeskAPI.getUsers();
      setUsers(response.data.results || response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  useEffect(() => {
    if (showAssignModal) {
      loadUsers();
    }
  }, [showAssignModal]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'bg-warning text-dark';
      case 'in_progress':
        return 'bg-info';
      case 'resolved':
        return 'bg-success';
      case 'closed':
        return 'bg-secondary';
      case 'pending_customer':
        return 'bg-warning';
      case 'pending_internal':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="me-2" />
          <span>Loading ticket...</span>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="main-content">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Ticket</Alert.Heading>
          <p>{error}</p>
          <hr />
          <Button variant="outline-danger" onClick={() => navigate('/tickets')}>
            <i className="fas fa-arrow-left me-2"></i>
            Back to Tickets
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="main-content ticket-detail-screen">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <div className="ticket-detail">
        {/* Sidebar - Left Side */}
        <div className={`ticket-sidebar ${isMobileLayout && !showTicketInfo ? 'ticket-sidebar-collapsed' : ''}`}>
          <Card>
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Ticket Information</h6>
                <div className="d-flex align-items-center gap-2">
                  {isMobileLayout && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="ticket-mobile-toggle"
                      onClick={() => setShowTicketInfo(false)}
                    >
                      Hide
                    </Button>
                  )}
                  <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={() => navigate('/tickets')}
                  title="Back to tickets"
                >
                  <i className="fas fa-arrow-left"></i>
                </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="ticket-sidebar-body">
              {/* Ticket Number & Status */}
              <div className="ticket-info-section">
                <small className="text-muted d-block ticket-info-label">Ticket</small>
                <div className="d-flex justify-content-between align-items-center gap-2">
                  <strong className="ticket-number-label">#{ticket.ticket_number}</strong>
                  <Badge className={getStatusBadgeClass(ticket.status)}>
                    {ticket.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="ticket-action-grid">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShowAssignModal(true)}
                  disabled={actionLoading}
                >
                  <i className="fas fa-user-plus me-2"></i>
                  Assign Ticket
                </Button>
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => {
                    setSelectedStatus(ticket.status);
                    setShowStatusModal(true);
                  }}
                  disabled={actionLoading}
                >
                  <i className="fas fa-exchange-alt me-2"></i>
                  Change Status
                </Button>
                {ticket.status === 'closed' ? (
                  <Button 
                    variant="success" 
                    size="sm"
                    onClick={handleReopenTicket}
                    disabled={actionLoading}
                  >
                    <i className="fas fa-redo me-2"></i>
                    Reopen Ticket
                  </Button>
                ) : (
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={handleCloseTicket}
                    disabled={actionLoading}
                  >
                    <i className="fas fa-times me-2"></i>
                    Close Ticket
                  </Button>
                )}
              </div>

              {/* Description */}
              <div className="ticket-info-section">
                <small className="text-muted d-block mb-2 ticket-info-label">
                  <i className="fas fa-align-left me-1"></i>
                  Description
                </small>
                <p className="mb-0 ticket-description-text" style={{ whiteSpace: 'pre-wrap' }}>
                  {ticket.description}
                </p>
              </div>

              <div className="ticket-meta-grid">
              <div className="ticket-meta-card">
                <small className="text-muted d-block ticket-info-label">Priority</small>
                <Badge
                  className="ticket-priority-pill"
                  style={{
                    backgroundColor: ticket.priority?.color || '#6c757d',
                  }}
                >
                  {ticket.priority?.name || 'Unknown'}
                </Badge>
              </div>

              <div className="ticket-meta-card">
                <small className="text-muted d-block ticket-info-label">Category</small>
                <strong>{ticket.category?.name || 'N/A'}</strong>
              </div>
              
              <div className="ticket-meta-card">
                <small className="text-muted d-block ticket-info-label">Created By</small>
                <strong>{ticket.created_by_name || 'Unknown'}</strong>
              </div>
              
              {ticket.assigned_to_name && (
                <div className="ticket-meta-card">
                  <small className="text-muted d-block ticket-info-label">Assigned To</small>
                  <strong>{ticket.assigned_to_name}</strong>
                </div>
              )}
              
              <div className="ticket-meta-card">
                <small className="text-muted d-block ticket-info-label">Created</small>
                <strong>{new Date(ticket.created_at).toLocaleString()}</strong>
              </div>
              
              <div className="ticket-meta-card">
                <small className="text-muted d-block ticket-info-label">Last Updated</small>
                <strong>{new Date(ticket.updated_at).toLocaleString()}</strong>
              </div>
              
              {ticket.resolved_at && (
                <div className="ticket-meta-card">
                  <small className="text-muted d-block ticket-info-label">Resolved</small>
                  <strong className="text-success">
                    {new Date(ticket.resolved_at).toLocaleString()}
                  </strong>
                </div>
              )}
              
              {ticket.closed_at && (
                <div className="ticket-meta-card">
                  <small className="text-muted d-block ticket-info-label">Closed</small>
                  <strong className="text-secondary">
                    {new Date(ticket.closed_at).toLocaleString()}
                  </strong>
                </div>
              )}
              </div>

              {latestAISupport && (
                <div className="ai-support-panel mt-4 pt-3 border-top">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <small className="text-muted d-block">AI Copilot</small>
                      <strong>Latest Triage</strong>
                    </div>
                    <Badge bg={latestAISupport.provider === 'openai' ? 'primary' : 'secondary'}>
                      {latestAISupport.provider || 'ai'}
                    </Badge>
                  </div>

                  <div className="ai-support-summary">
                    {latestAISupport.summary || 'No AI summary available yet.'}
                  </div>

                  <div className="ai-support-meta">
                    <div className="ai-support-meta-item">
                      <span className="ai-support-label">Issue</span>
                      <strong>{(latestAISupport.issue_type || 'unknown').replace(/_/g, ' ')}</strong>
                    </div>
                    <div className="ai-support-meta-item">
                      <span className="ai-support-label">Module</span>
                      <strong>{latestAISupport.module || 'unknown'}</strong>
                    </div>
                    <div className="ai-support-meta-item">
                      <span className="ai-support-label">Severity</span>
                      <Badge bg={
                        latestAISupport.severity === 'critical' ? 'danger' :
                        latestAISupport.severity === 'high' ? 'warning' :
                        latestAISupport.severity === 'medium' ? 'info' : 'secondary'
                      }>
                        {latestAISupport.severity || 'unknown'}
                      </Badge>
                    </div>
                    <div className="ai-support-meta-item">
                      <span className="ai-support-label">Confidence</span>
                      <strong>{latestAISupport.confidence || 'unknown'}</strong>
                    </div>
                    <div className="ai-support-meta-item">
                      <span className="ai-support-label">Basis</span>
                      <strong>{(latestAISupport.analysis_basis || 'latest_message_only').replace(/_/g, ' ')}</strong>
                    </div>
                    <div className="ai-support-meta-item">
                      <span className="ai-support-label">Escalate</span>
                      <strong className={latestAISupport.should_escalate ? 'text-danger' : 'text-success'}>
                        {latestAISupport.should_escalate ? 'Yes' : 'No'}
                      </strong>
                    </div>
                  </div>

                  {latestAISupport.clarifying_questions?.length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted d-block mb-2">Suggested Questions</small>
                      <ul className="ai-support-list mb-0">
                        {latestAISupport.clarifying_questions.map((question, index) => (
                          <li key={`${index}-${question}`}>{question}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {latestAISupport.draft_reply && (
                    <div className="mt-3">
                      <small className="text-muted d-block mb-2">Draft Reply</small>
                      <div className="ai-support-draft">{latestAISupport.draft_reply}</div>
                      <div className="d-grid mt-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleUseAIDraftReply(latestAISupport.draft_reply)}
                        >
                          <i className="fas fa-wand-magic-sparkles me-2"></i>
                          Use Draft Reply
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {isMobileLayout && showTicketInfo && (
          <button
            type="button"
            className="ticket-sidebar-backdrop"
            aria-label="Close ticket details"
            onClick={() => setShowTicketInfo(false)}
          />
        )}

        {/* Chat Section - Right Side */}
        <div className="ticket-main">
          <div className="ticket-mobile-summary">
            <div className="ticket-mobile-summary-top">
              <div className="ticket-mobile-summary-main">
                <div className="ticket-mobile-ticket-number">#{ticket.ticket_number}</div>
                <div className="ticket-mobile-ticket-title">{ticket.title}</div>
              </div>
              <Badge className={`ticket-mobile-status ${getStatusBadgeClass(ticket.status)}`}>
                {ticket.status.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            {isMobileLayout && (
              <div className="ticket-mobile-summary-actions">
                <Button
                  variant="link"
                  size="sm"
                  className="ticket-mobile-toggle"
                  onClick={() => setShowTicketInfo((prev) => !prev)}
                >
                  <i className={`fas ${showTicketInfo ? 'fa-eye-slash' : 'fa-eye'} me-2`}></i>
                  {showTicketInfo ? 'Hide Details' : 'Show Details'}
                </Button>
              </div>
            )}
          </div>

          {hasNewMessage && (
            <div className="ticket-floating-notice">
              <i className="fas fa-bell me-2"></i>
              New message received
            </div>
          )}
          <Card className="shadow-sm border-0 ticket-conversation-card">
            <div className="ticket-conversation-shell">
              {/* Auto-refresh indicator */}
              <div className="ticket-live-indicator">
                <span className="ticket-live-indicator-dot"></span>
                Auto-updating every 10s
              </div>
              
              {ticket.description || (ticket.comments && ticket.comments.length > 0) ? (
                <div className="chat-messages-area">
                    {/* The customer's original message always opens the thread */}
                    {ticket.description && (
                      <div className="chat-message chat-message-incoming chat-message-opening">
                        <div className="chat-message-bubble">
                          <div className="chat-message-header-row">
                            <span className="chat-message-author">{ticket.created_by_name || 'Customer'}</span>
                            <span className="chat-channel-badge">
                              <i className={`${CHANNEL_ICON_CLASSES[ticket.channel] || 'fas fa-comment'} me-1`}></i>
                              {CHANNEL_LABELS[ticket.channel] || 'Web'}
                            </span>
                          </div>
                          <div className="chat-message-content">
                            {ticket.description}
                          </div>
                          <div className="chat-message-time">
                            {new Date(ticket.created_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {ticket.comments && ticket.comments.map((comment) => {
                      const isCustomer = comment.user_name === ticket.created_by_name;

                      // AI-authored internal notes render as a compact card, not a bubble
                      if (comment.ai_analysis) {
                        return (
                          <AICopilotCard
                            key={comment.id}
                            analysis={comment.ai_analysis}
                            timestamp={comment.created_at}
                            onUseDraftReply={handleUseAIDraftReply}
                          />
                        );
                      }

                      return (
                        <div
                          key={comment.id}
                          className={`chat-message ${isCustomer ? 'chat-message-incoming' : 'chat-message-outgoing'} ${comment.is_internal ? 'chat-message-internal' : ''}`}
                        >
                          <div className="chat-message-bubble">
                            <div className="chat-message-header-row">
                              <span className="chat-message-author">{comment.user_name}</span>
                              {comment.is_internal && (
                                <Badge bg="secondary" className="chat-internal-badge">
                                  <i className="fas fa-lock me-1"></i>
                                  Internal
                                </Badge>
                              )}
                            </div>
                            <div className="chat-message-content">
                              {comment.content}
                            </div>

                            {/* Show recent attachments near this comment */}
                            {ticket.attachments && ticket.attachments.some(att => {
                              const attTime = new Date(att.uploaded_at).getTime();
                              const commentTime = new Date(comment.created_at).getTime();
                              return Math.abs(attTime - commentTime) < 10000; // Within 10 seconds
                            }) && (
                              <div className="chat-message-attachments">
                                {ticket.attachments.filter(att => {
                                  const attTime = new Date(att.uploaded_at).getTime();
                                  const commentTime = new Date(comment.created_at).getTime();
                                  return Math.abs(attTime - commentTime) < 10000;
                                }).map(attachment => {
                                  const fileUrl = buildMediaUrl(attachment.file);

                                  return (
                                    <div key={attachment.id} className="chat-attachment">
                                      {attachment.content_type && attachment.content_type.startsWith('image/') ? (
                                        <div className="chat-message-image">
                                          <img
                                            src={fileUrl}
                                            alt={attachment.filename}
                                            onClick={() => window.open(fileUrl, '_blank')}
                                            loading="lazy"
                                          />
                                        </div>
                                      ) : (
                                        <div className="chat-message-file">
                                          <div className="chat-message-file-icon">
                                            <i className="fas fa-file"></i>
                                          </div>
                                          <div className="chat-message-file-info">
                                            <div className="chat-message-file-name">{attachment.filename}</div>
                                          </div>
                                          <a
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-link"
                                          >
                                            <i className="fas fa-download"></i>
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <div className="chat-message-time">
                              {new Date(comment.created_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Invisible div for auto-scroll */}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <div className="chat-empty-state">
                    <i className="fas fa-comments"></i>
                    <p>No messages yet</p>
                    <small>Start the conversation!</small>
                  </div>
                )}
              </div>
            
            {/* Telegram-style Compact Input */}
            <div className="chat-input-container" style={{ flexShrink: 0 }}>
              {selectedFiles.length > 0 && (
                <div className="chat-attachment-preview">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="chat-attachment-item">
                      <i className="fas fa-paperclip"></i>
                      <span className="chat-attachment-name">{file.name}</span>
                      <button
                        type="button"
                        className="chat-attachment-remove"
                        onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}
                        title="Remove file"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <Form onSubmit={handleAddComment}>
                <div className="chat-input-wrapper">
                  {/* Paperclip button */}
                  <label className="chat-attach-btn" title="Attach file">
                    <i className="fas fa-paperclip"></i>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                      disabled={submittingComment}
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      style={{ display: 'none' }}
                    />
                  </label>
                  
                  {/* Auto-expanding textarea */}
                  <textarea
                    className="chat-input-field"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment(e);
                      }
                    }}
                    placeholder="Type a message..."
                    disabled={submittingComment}
                    rows={1}
                  />
                  
                  {/* Send button */}
                  <button 
                    type="submit" 
                    className="chat-send-btn"
                    disabled={submittingComment || (!commentText.trim() && selectedFiles.length === 0)}
                    onClick={handleAddComment}
                    title="Send message"
                  >
                    {submittingComment ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className="fas fa-paper-plane"></i>
                    )}
                  </button>
                </div>
                
                {/* Internal note + save-as-knowledge checkboxes below input */}
                <div className="chat-input-options d-flex flex-wrap gap-3">
                  <Form.Check
                    type="checkbox"
                    id="internal-note-check"
                    label="Internal note (hidden from customer)"
                    checked={isInternalNote}
                    onChange={(e) => setIsInternalNote(e.target.checked)}
                    disabled={submittingComment}
                    className="chat-internal-checkbox"
                  />
                  {!isInternalNote && (
                    <Form.Check
                      type="checkbox"
                      id="save-as-knowledge-check"
                      label="Save as knowledge"
                      checked={saveAsKnowledge}
                      onChange={(e) => setSaveAsKnowledge(e.target.checked)}
                      disabled={submittingComment}
                      className="chat-internal-checkbox"
                    />
                  )}
                </div>
              </Form>
            </div>
          </Card>
        </div>
      </div>

      {/* Assign Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Assign To</Form.Label>
            <Form.Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="">Select user...</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignTicket}
            disabled={actionLoading || !selectedUser}
          >
            {actionLoading ? 'Assigning...' : 'Assign'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Status Change Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New Status</Form.Label>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">Select status...</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_customer">Pending Customer</option>
              <option value="pending_internal">Pending Internal</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleChangeStatus}
            disabled={actionLoading || !selectedStatus}
          >
            {actionLoading ? 'Changing...' : 'Change Status'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TicketDetail;
